import type { ApiResponse } from '@/libs/interfaces'

const rawApiBaseUrl: unknown = import.meta.env.VITE_API_BASE_URL
const API_BASE_URL =
  typeof rawApiBaseUrl === 'string' && rawApiBaseUrl.length > 0 ? rawApiBaseUrl : '/api'

interface RequestConfig extends Omit<RequestInit, 'body'> {
  params?: Record<string, string>
  body?: unknown
  signal?: AbortSignal
  /** Skip the automatic access-denied redirect on 401/403 — let the caller handle the error itself. */
  skipErrorRedirect?: boolean
}

class ApiError<T = unknown> extends Error {
  status: number
  data?: T

  constructor(message: string, status: number, data?: T) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.data = data
  }
}

const getAuthToken = () => localStorage.getItem('authToken')
const getRefreshToken = () => localStorage.getItem('refreshToken')

export const setAuthToken = (token: string | null) => {
  if (token) {
    localStorage.setItem('authToken', token)
  } else {
    localStorage.removeItem('authToken')
  }
}

export const setRefreshToken = (token: string | null) => {
  if (token) localStorage.setItem('refreshToken', token)
  else localStorage.removeItem('refreshToken')
}

const buildUrl = (endpoint: string, params?: Record<string, string>) => {
  const url = new URL(`${API_BASE_URL}${endpoint}`, window.location.origin)
  if (params) {
    Object.entries(params).forEach(([k, v]) => {
      url.searchParams.append(k, v)
    })
  }
  return url.toString()
}

const handleResponse = async <T>(response: Response): Promise<ApiResponse<T>> => {
  const isJson = response.headers.get('content-type')?.includes('application/json')
  const data: unknown = isJson ? await response.json() : await response.text()
  const dataObj = data as Record<string, unknown>

  if (!response.ok) {
    throw new ApiError(
      (typeof dataObj?.message === 'string' ? dataObj.message : null) ||
        `HTTP error ${response.status}`,
      response.status,
      data,
    )
  }

  return {
    data: data as T,
    status: response.status,
    message: typeof dataObj?.message === 'string' ? dataObj.message : undefined,
  }
}

// Queue to hold pending requests while a token refresh is in progress
let isRefreshing = false
let refreshQueue: Array<(token: string | null) => void> = []

const processRefreshQueue = (token: string | null) => {
  refreshQueue.forEach((resolve) => resolve(token))
  refreshQueue = []
}

const doTokenRefresh = async (): Promise<string | null> => {
  const currentRefreshToken = getRefreshToken()
  if (!currentRefreshToken) return null

  const res = await fetch(`${API_BASE_URL}/refresh-token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ refreshToken: currentRefreshToken }),
  })

  const data = (await res.json()) as Record<string, unknown>

  if (!res.ok) throw new Error('Refresh failed')

  const newToken = typeof data.accessToken === 'string' ? data.accessToken : ''
  setAuthToken(newToken)

  if (typeof data.refreshToken === 'string') {
    setRefreshToken(data.refreshToken)
  }

  return newToken
}

// Use replace (not href) so the denied page's own history entry is
// overwritten instead of having /access-denied pushed on top of it —
// otherwise the browser Back button lands back on the denied page, which
// refetches, gets 403 again, and redirects forward in an endless loop.
const redirectToAccessDenied = () => {
  window.location.replace('/access-denied')
}

// Refresh token is invalid/expired — the session itself is over, not just one
// request. Clear all local auth state before leaving, otherwise the app still
// looks "authenticated" on the next render and immediately refires every API
// call, hitting 401 again in a redirect loop.
const redirectToLogin = () => {
  localStorage.clear()
  sessionStorage.clear()
  window.location.replace('/login')
}

const refreshAccessToken = async (): Promise<string | null> => {
  if (isRefreshing) {
    // Queue this request until the in-flight refresh resolves
    return new Promise<string | null>((resolve) => {
      refreshQueue.push(resolve)
    })
  }

  isRefreshing = true

  try {
    const newToken = await doTokenRefresh()
    if (!newToken) {
      processRefreshQueue(null)
      redirectToLogin()
      return null
    }
    processRefreshQueue(newToken)
    return newToken
  } catch {
    processRefreshQueue(null)
    redirectToLogin()
    return null
  } finally {
    isRefreshing = false
  }
}

const buildRequestHeaders = (
  configHeaders: HeadersInit | undefined,
  body: unknown,
  isFormData: boolean,
  token: string | null,
): HeadersInit => ({
  ...configHeaders,
  ...(body !== undefined && !isFormData && { 'Content-Type': 'application/json' }),
  ...(token && { Authorization: `Bearer ${token}` }),
})

const serializeRequestBody = (body: unknown, isFormData: boolean): BodyInit | undefined => {
  if (!body) return undefined
  return isFormData ? (body as BodyInit) : JSON.stringify(body)
}

const isAuthExpiredStatus = (status: number, retry: boolean): boolean =>
  (status === 401 || status === 403) && retry

const isAccessDeniedError = (error: unknown, skipErrorRedirect?: boolean): boolean =>
  !skipErrorRedirect && error instanceof ApiError && (error.status === 401 || error.status === 403)

const retryRequestAfterRefresh = async <T>(
  endpoint: string,
  config: RequestConfig,
): Promise<ApiResponse<T>> => {
  try {
    return await createRequest<T>(endpoint, config, false)
  } catch (error) {
    if (isAccessDeniedError(error, config.skipErrorRedirect)) {
      redirectToAccessDenied()
    }
    throw error
  }
}

const createRequest = async <T>(
  endpoint: string,
  config: RequestConfig = {},
  retry = true,
): Promise<ApiResponse<T>> => {
  const { params, body, signal, ...rest } = config

  const url = buildUrl(endpoint, params)
  const token = getAuthToken()

  const isFormData = body instanceof FormData
  const headers = buildRequestHeaders(rest.headers, body, isFormData, token)

  const response = await fetch(url, {
    ...rest,
    headers,
    signal,
    body: serializeRequestBody(body, isFormData),
  })

  if (isAuthExpiredStatus(response.status, retry)) {
    const newToken = await refreshAccessToken()
    if (newToken) {
      return retryRequestAfterRefresh<T>(endpoint, config)
    }
  }

  return handleResponse<T>(response)
}

export const api = {
  get: <T>(
    endpoint: string,
    params?: Record<string, string>,
    signal?: AbortSignal,
    skipErrorRedirect?: boolean,
  ) =>
    createRequest<T>(endpoint, {
      method: 'GET',
      params,
      signal,
      skipErrorRedirect,
    }),

  post: <T>(endpoint: string, data?: unknown, signal?: AbortSignal) =>
    createRequest<T>(endpoint, { method: 'POST', body: data, signal }),

  put: <T>(endpoint: string, data?: unknown, signal?: AbortSignal) =>
    createRequest<T>(endpoint, { method: 'PUT', body: data, signal }),

  patch: <T>(endpoint: string, data?: unknown, signal?: AbortSignal) =>
    createRequest<T>(endpoint, { method: 'PATCH', body: data, signal }),

  delete: <T>(endpoint: string, data?: unknown, signal?: AbortSignal) =>
    createRequest<T>(endpoint, { method: 'DELETE', body: data, signal }),
}

export { ApiError }
